import React from "react";
import { Instagram, Linkedin, Facebook, MessageCircle, ArrowUpRight, Phone, Mail, MapPin } from "lucide-react";
import { useLang } from "../i18n";
import { CONTACT, SOCIALS, CLIENT_AREA_URL, buildWhatsAppLink } from "../lib/constants";
import { scrollTo } from "./Header";

export const Footer = () => {
  const { t } = useLang();
  const year = new Date().getFullYear();

  const navLinks = [
    { id: "servicos", label: t.nav.services },
    { id: "sobre", label: t.nav.about },
    { id: "clientes", label: t.nav.clients },
    { id: "contato", label: t.nav.contact },
  ];

  const socials = [
    { icon: Facebook, href: SOCIALS.facebook, label: "Facebook" },
    { icon: Instagram, href: SOCIALS.instagram, label: "Instagram" },
    { icon: Linkedin, href: SOCIALS.linkedin, label: "LinkedIn" },
    { icon: MessageCircle, href: SOCIALS.whatsapp, label: "WhatsApp" },
  ];

  return (
    <footer data-testid="site-footer" className="relative border-t border-white/10 bg-[#050507] pt-16 pb-8 noise-overlay">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-5">
            <img src={`${process.env.PUBLIC_URL}/images/waltech-logo.png`} alt="Waltech" className="h-10 w-auto object-contain" />
            <p className="mt-5 max-w-sm text-sm font-light leading-relaxed text-zinc-400">
              {t.footer.desc}
            </p>
            <div className="mt-6 flex items-center gap-3">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-zinc-300 hover:text-white hover:border-[#00e5ff]/50 hover:-translate-y-0.5 transition-[color,border-color,transform] duration-300"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Nav */}
          <div className="md:col-span-3">
            <div className="font-mono-wt text-xs uppercase tracking-[0.2em] text-zinc-500">
              {t.footer.navTitle}
            </div>
            <ul className="mt-4 space-y-3">
              {navLinks.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => scrollTo(l.id)}
                    data-testid={`footer-nav-${l.id}`}
                    className="text-sm text-zinc-400 hover:text-white transition-colors duration-300"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
              <li>
                <a
                  href={CLIENT_AREA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-client-area"
                  className="inline-flex items-center gap-1 text-sm text-[#00e5ff] hover:text-white transition-colors duration-300"
                >
                  {t.nav.clientArea}
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <div className="font-mono-wt text-xs uppercase tracking-[0.2em] text-zinc-500">
              {t.footer.contactTitle}
            </div>
            <ul className="mt-4 space-y-3 text-sm text-zinc-400">
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-[#00e5ff]" />
                <a href={buildWhatsAppLink(t.whatsapp.floatMsg)} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors duration-300">
                  {CONTACT.phone1}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-[#00e5ff]" />
                <a href="tel:+551123910229" className="hover:text-white transition-colors duration-300">
                  {CONTACT.phone2}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-[#00e5ff]" />
                <a href={`mailto:${CONTACT.email}`} className="hover:text-white transition-colors duration-300">
                  {CONTACT.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 shrink-0 text-[#00e5ff] mt-0.5" />
                <span>
                  {CONTACT.addressLine1}, {CONTACT.addressLine2} — {CONTACT.addressLine3}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-6 text-center">
          <p className="text-xs text-zinc-500">
            © {year} Jeferson Davi. {t.footer.rights}
          </p>
        </div>
      </div>
    </footer>
  );
};
